import {Injectable, NgZone} from '@angular/core';
import {first, map, Observable, ReplaySubject, throwError, timeout} from "rxjs";
import {catchError} from "rxjs/operators";
import StringUtils from "../utils/string-utils";
import {InvoicePosition} from "../dto/invoice-position";

@Injectable({
  providedIn: 'root'
})
export class ParentWindowService {
  private parentHrefSubject = new ReplaySubject<string>(1);
  public parentHrefObservable: Observable<string>;
  private maxWaitTime: number = 2000; // maxWaitTime in milliseconds

  private static ORIGIN_REQUEST: string = 'ORIGIN_REQUEST';
  private static ORIGIN_RESPONSE: string = 'ORIGIN_RESPONSE';
  private static ORDER_CONFIRMATION_CALLBACK: string = 'ORDER_CONFIRMATION_CALLBACK';
  private static NAVIGATION_CALLBACK: string = 'NAVIGATION_CALLBACK';

  constructor(private ngZone: NgZone) {
    this.listenForMessages();
    this.parentHrefObservable = this.parentHrefSubject.asObservable();
  }

  /**
   * Sends a message to the parent window to request data
   * Note: A script is required within the parent window handling the messages
   * @example Script to receive the posted message and send the requested answer:
   *
   * <script>
   *    window.addEventListener('message', function(event) {
   *      if (event.data.type === 'ORIGIN_REQUEST') {
   *        // Send the parent window's current url/href to the iframe
   *        const message = { type: 'ORIGIN_RESPONSE', href: window.location.href };
   *        document.getElementById('de-iframe').contentWindow.postMessage(message, event.origin);
   *      }
   *    });
   * </script>
   */
  requestParentWindowUrl(): Observable<string> {
    // Request parent href and return the domain if it contains one
    const message = { type: ParentWindowService.ORIGIN_REQUEST };
    window.parent.postMessage(message, '*');
    return this.parentHrefObservable.pipe(
      first(),
      map(originResponse => {
        return StringUtils.getDomainFromUrl(originResponse) || 'UNKNOWN';
      }),
      timeout(this.maxWaitTime),
      catchError(err => {
        if (err.name === 'TimeoutError') {
          return throwError('No response from origin received!');
        }
        return throwError(err);
      })
    ); // Take only the first emission
  }

  /**
   * Sends the given data via the PostMessageAPI to the parent window
   * @param orderId
   * @param invoicePositions
   * @example Script to receive the posted message:
   *
   * <script>
   *    window.addEventListener('message', function(event) {
   *      if (event.data.type === 'ORDER_CONFIRMATION_CALLBACK') {
   *        // your code to handle the event
   *      }
   *    });
   * </script>
   */
  sendOrderConfirmationCallback(orderId: string, invoicePositions: InvoicePosition[]): void {
    const message = { type: ParentWindowService.ORDER_CONFIRMATION_CALLBACK, orderId : orderId, invoicePositions: invoicePositions };
    window.parent.postMessage(message, '*');
  }

  /**
   * Sends a NAVIGATION_CALLBACK to the parent window to allowing it to react to wanted parent window navigation
   * @example Script to receive the posted message:
   *
   * <script>
   *    window.addEventListener('message', function(event) {
   *      if (event.data.type === 'NAVIGATION_CALLBACK') {
   *        // your code to handle the navigation event
   *      }
   *    });
   * </script>
   */
  sendNavigationCallback(): void {
    const message = { type: ParentWindowService.NAVIGATION_CALLBACK };
    window.parent.postMessage(message, '*');
  }

  /**
   * Listens for all kinds of messages, but triggers only on the 'ORIGIN_RESPONSE' event type
   * @private
   */
  private listenForMessages(): void {
    window.addEventListener('message', (event) => {
      // Check the message type to filter out unwanted messages
      if (event.data?.type === ParentWindowService.ORIGIN_RESPONSE) {
        this.ngZone.run(() => {
          this.parentHrefSubject.next(event.data.href);
        });
      }
    });
  }
}
